import { Platform } from 'react-native';
import { functions, auth, httpsCallable } from '../config/firebase';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { FeedbackRequest, FeedbackRequestSchema, FeedbackResponse, FeedbackResponseSchema } from '../utils/zod/feedbackSchemas';
import { loggingService } from './loggingService';
import { errorReporting } from './errorReportingService';

const EMAIL_STORAGE_KEY = 'feedback_user_email';
const EMAIL_TTL_MS = 180 * 24 * 60 * 60 * 1000; // 180 days

type ServiceResponse<T> = { success: boolean; data?: T; error?: string };

async function getStoredEmail(): Promise<string | null> {
  try {
    const raw = await AsyncStorage.getItem(EMAIL_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.email || !parsed?.savedAt) return null;
    if (Date.now() - parsed.savedAt > EMAIL_TTL_MS) {
      await AsyncStorage.removeItem(EMAIL_STORAGE_KEY);
      return null;
    }
    return parsed.email as string;
  } catch {
    return null;
  }
}

async function saveEmail(email: string | undefined | null): Promise<void> {
  if (!email) return;
  try {
    await AsyncStorage.setItem(
      EMAIL_STORAGE_KEY,
      JSON.stringify({ email, savedAt: Date.now() })
    );
  } catch {}
}

export class FeedbackService {
  static async getPrefilledEmail(): Promise<string | null> {
    return getStoredEmail();
  }

  private static getClientMeta(): {
    appVersion?: string;
    buildNumber?: string;
    device?: { model?: string; osVersion?: string };
  } {
    let appVersion: string | undefined;
    let buildNumber: string | undefined;
    let deviceModel: string | undefined;
    let osVersion: string | undefined;

    // Version/build via Expo Constants when available
    try {
      const Constants = require('expo-constants').default;
      appVersion = Constants?.expoConfig?.version || Constants?.manifest?.version;
      // iOS buildNumber is string, Android versionCode is number
      const iosBuild = Constants?.expoConfig?.ios?.buildNumber;
      const androidCode = Constants?.expoConfig?.android?.versionCode;
      buildNumber = iosBuild || (androidCode ? String(androidCode) : undefined);
    } catch {}

    // Device info
    try {
      if (Platform.OS === 'web') {
        const nav: any = (globalThis as any)?.navigator;
        deviceModel = nav?.userAgent || 'Web Browser';
        osVersion = nav?.userAgentData?.platform || nav?.platform || undefined;
      } else {
        deviceModel = Platform.OS; // No extra native deps; keep minimal
        osVersion = String(Platform.Version);
      }
    } catch {}

    const meta: any = {};
    if (appVersion) meta.appVersion = appVersion;
    if (buildNumber) meta.buildNumber = buildNumber;
    const device: any = {};
    if (deviceModel) device.model = deviceModel;
    if (osVersion) device.osVersion = osVersion;
    if (Object.keys(device).length > 0) meta.device = device;
    return meta;
  }

  static async sendFeedback(payload: FeedbackRequest): Promise<ServiceResponse<{ feedbackId: string }>> {
    try {
      const validated = FeedbackRequestSchema.parse(payload);

      // Enrich client params minimally; server will add platform/appVersion/userId
      // Note: userId is automatically available to server via request.auth?.uid in Firebase callable functions
      // The function already includes it in the admin email automatically
      const requestBody = {
        ...validated,
        platform: Platform.OS === 'web' ? 'web' : Platform.OS,
        ...FeedbackService.getClientMeta(),
      };

      // Remove null/undefined to satisfy strict server schema (no nulls allowed)
      const removeNullishDeep = (obj: any): any => {
        if (Array.isArray(obj)) return obj.map(removeNullishDeep);
        if (obj && typeof obj === 'object') {
          const out: any = {};
          Object.entries(obj).forEach(([k, v]) => {
            if (v !== undefined && v !== null) {
              out[k] = removeNullishDeep(v);
            }
          });
          return out;
        }
        return obj;
      };
      const cleanBody = removeNullishDeep(requestBody);

      // Cross-platform callable invocation
      const call = httpsCallable(functions, 'submitFeedback');
      const callableResult = await call(cleanBody);

      const data = (callableResult?.data ?? callableResult) as FeedbackResponse;
      const parsed = FeedbackResponseSchema.safeParse(data);
      if (!parsed.success) {
        errorReporting.reportApiValidationError(
          'submitFeedback',
          FeedbackResponseSchema.shape,
          data,
          { screen: 'ContactUsScreen', action: 'submitFeedback' }
        );
        return { success: false, error: 'Invalid response from server' };
      }

      // Persist email for future convenience
      await saveEmail(validated.email);

      // Log analytics without PII
      await loggingService.log({
        type: require('../utils/logging/actions').LogActionType.FORM_SUBMITTED,
        timestamp: Date.now(),
        itemId: 'contact_us',
        params: {
          messageLength: validated.message.length,
          hasEmail: Boolean(validated.email),
          feedbackId: parsed.data.feedbackId,
        },
      });

      return { success: true, data: { feedbackId: parsed.data.feedbackId } };
    } catch (error: any) {
      const message = error?.message || 'Failed to send feedback';
      errorReporting.reportError(error, {
        screen: 'ContactUsScreen',
        action: 'submitFeedback',
        metadata: { userId: auth?.currentUser?.uid, platform: Platform.OS },
      });
      await loggingService.log({
        type: require('../utils/logging/actions').LogActionType.ERROR_OCCURRED,
        timestamp: Date.now(),
        itemId: 'contact_us_submit',
        params: { errorMessage: message },
      });
      return { success: false, error: message };
    }
  }
}


